import {fiscalJobItemReason, FiscalJobItem} from '../fiscal-jobs/fiscal-job.models';
import {formatDay} from './data-export.models';

/**
 * The TSE data export as the screen reads it (#776) — the wire types this surface adds on top of the fiscal
 * job engine's, plus the German words the screen puts on them.
 *
 * ### Why this is a second file and not a widening of `data-export.models.ts`
 *
 * The two exports are different artefacts in law: § 4 KassenSichV Satz 1 requires both the cash-register data
 * (DSFinV-K) and the security device's own signed log messages, and Satz 4 permits the two interfaces to be
 * published separately. They are requested differently (no register filter here at all), refused for
 * different reasons, and an operator is sent to exactly one of them by whoever asked for the file. A shared
 * map would force every sentence to be written so it is true of both, which is how an instruction becomes
 * vague enough to be useless.
 *
 * What genuinely IS the same is imported rather than copied: {@link formatDay} for a day, and the engine's own
 * state labels and tones, which belong to the engine and not to either export.
 *
 * ### Nothing here names a provider
 *
 * That is the point of Feature #241. The export is described by **what it is** (the data of the technical
 * security device — the law's own term, § 146a AO) and **what period it covers**, never by who operates it.
 */

/** The period a TSE export run covers, as the run stored it. */
export interface TseExportPeriod {
  /** The first day the operator chose, `yyyy-MM-dd`. */
  from: string;
  /** The last day the operator chose, inclusive, `yyyy-MM-dd`. */
  to: string;
  /**
   * The first instant actually covered, as epoch seconds.
   *
   * Carried because it is what was really sent: the security device holds log messages stamped with the
   * instant each signature was created, and knows nothing about trading days. Not rendered — a screen showing
   * an epoch number answers nobody's question — but read back so a run's window is a stored fact rather than
   * something re-derived from a time zone that may have changed.
   */
  startEpoch: number;
  /** The last instant actually covered, inclusive, as epoch seconds. */
  endEpoch: number;
}

/** What starting a TSE data export asks for. Two days and nothing else. */
export interface TseExportCreateRequest {
  /** The first day, `yyyy-MM-dd`. */
  from: string;
  /** The last day, inclusive, `yyyy-MM-dd`. */
  to: string;
}

/** A collected TSE export: the bytes and the name the server chose for them. */
export interface TseExportFile {
  fileName: string;
  blob: Blob;
}

/**
 * What one security device is called on screen.
 *
 * Mirrors `FiscalTseExportSelection.DeviceLabel` server-side, which also names the downloaded file. The short
 * form is deliberate there (the label is slugged into a file name and capped), and it is used here so the row
 * on screen and the file in the download folder say the same thing.
 */
export const TSE_DEVICE_LABEL = 'Sicherheitseinrichtung';

/**
 * Reads a run's stored parameters back into the period it covers.
 *
 * **Tolerant on purpose.** The blob is written by one build and read by the next, and a run whose parameters a
 * later build cannot read has to degrade into „Zeitraum unbekannt" on a row rather than take the whole list
 * down with it. The instants are required though — a run with no window is not a run this screen can describe,
 * and quietly showing its days while the export covered something else would be worse than saying nothing.
 */
export function parseTseExportPeriod(parameters: string | null | undefined): TseExportPeriod | null {
  if (!parameters) return null;

  try {
    const parsed: unknown = JSON.parse(parameters);
    if (!parsed || typeof parsed !== 'object') return null;

    const {from, to, startEpoch, endEpoch} = parsed as Record<string, unknown>;
    if (typeof from !== 'string' || typeof to !== 'string') return null;
    if (!isIsoDay(from) || !isIsoDay(to)) return null;
    if (typeof startEpoch !== 'number' || typeof endEpoch !== 'number') return null;
    if (!Number.isFinite(startEpoch) || !Number.isFinite(endEpoch) || endEpoch <= startEpoch) return null;

    return {from, to, startEpoch, endEpoch};
  } catch {
    return null;
  }
}

/** A period as it is written on screen: `01.01.2026 – 31.01.2026`. */
export function formatTseExportPeriod(period: TseExportPeriod | null): string {
  if (!period) return 'Zeitraum unbekannt';
  // An en dash with spaces, the German convention for a range — not a hyphen, which reads as part of a date
  // that is already full of hyphens.
  return `${formatDay(period.from)} – ${formatDay(period.to)}`;
}

/** Whether a finished entry has a file to collect. */
export function isTseExportDownloadable(item: FiscalJobItem): boolean {
  return item.state === 'completed' && !!item.resultReference;
}

/**
 * The German sentence for an `E_FISCAL_TSE_*` code.
 *
 * The server's catalog descriptions are English — they are part of the API contract, branched on by their code
 * — so the screen owns the words. An unknown code falls back to a sentence that still tells the operator what
 * to do, because a code on screen is not an instruction.
 */
export function tseExportErrorMessage(code: string | null | undefined): string {
  return tseExportErrorSentence(code) ?? FALLBACK_ERROR;
}

/**
 * The German sentence for an `E_FISCAL_TSE_*` code, or `null` when this screen has none for it.
 *
 * The nullable half of {@link tseExportErrorMessage}. Mirrors `exportErrorSentence` on the DSFinV-K side, for
 * the reason recorded there: {@link tseExportItemReason} has to be able to fall through to the far end's own
 * explanation, and a lookup that always answers something never falls through.
 */
export function tseExportErrorSentence(code: string | null | undefined): string | null {
  if (!code) return null;
  return ERROR_MESSAGES[code] ?? null;
}

/**
 * Why one entry of a TSE export did not come out, in German.
 *
 * Our own sentence for the code first, the server's sanitised `errorDetail` second, a generic sentence last.
 * The order and the reasoning live in {@link fiscalJobItemReason}; this screen and the DSFinV-K export screen
 * both bind to it rather than each writing the precedence out, which is how the two had the same defect
 * (Bug #789).
 */
export function tseExportItemReason(item: FiscalJobItem): string {
  return fiscalJobItemReason(item, tseExportErrorSentence, FALLBACK_ERROR);
}

const FALLBACK_ERROR =
  'Der Vorgang konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut oder wenden Sie sich an die ' +
  'Administration.';

/**
 * Every refusal this screen can meet, in German and in the formal Sie.
 *
 * Each sentence says what happened **and** what to do next. A message that only states a rule leaves the
 * reader to guess the remedy, and the remedy is the whole reason they are reading it.
 */
const ERROR_MESSAGES: Record<string, string> = {
  // ----- the request -----
  E_FISCAL_TSE_EXPORT_PERIOD_INVALID:
    'Der gewählte Zeitraum ist nicht gültig. Das Bis-Datum darf nicht vor dem Von-Datum liegen.',
  // Says WHY the limit is a year and what to do, because the DSFinV-K export next door allows ten and an
  // operator who has used that one will otherwise read this as a fault.
  E_FISCAL_TSE_EXPORT_PERIOD_TOO_LONG:
    'Ein Export der Sicherheitseinrichtung umfasst höchstens ein Jahr. Bitte fordern Sie längere Zeiträume ' +
    'in Abschnitten von je einem Jahr an.',
  E_FISCAL_TSE_EXPORT_NOT_CONFIGURED:
    'Für diesen Standort ist keine Kassenführung eingerichtet. Es kann kein Export der Sicherheitseinrichtung ' +
    'erstellt werden.',
  // Eigener Satz neben NOT_CONFIGURED, weil der Satz darüber etwas über den Standort behauptet und dieser
  // nur über den Lesevorgang spricht (#829).
  E_FISCAL_TSE_EXPORT_SETTINGS_UNAVAILABLE:
    'Die Einstellungen zur Kassenführung konnten gerade nicht gelesen werden. Es ist nichts verloren. Bitte ' +
    'versuchen Sie es in Kürze erneut.',
  E_FISCAL_TSE_EXPORT_NO_TSS_IN_PERIOD:
    'Im gewählten Zeitraum war an diesem Standort keine Sicherheitseinrichtung im Einsatz. Es gibt deshalb ' +
    'nichts zu exportieren. Bitte wählen Sie einen anderen Zeitraum.',
  E_FISCAL_TSE_EXPORT_TSS_SET_UNAVAILABLE:
    'Es konnte gerade nicht ermittelt werden, welche Sicherheitseinrichtungen im gewählten Zeitraum im ' +
    'Einsatz waren. Es ist nichts verloren. Bitte versuchen Sie es in Kürze erneut.',

  // ----- what the far end says about one export -----
  E_FISCAL_TSE_EXPORT_BUSY:
    'Die Sicherheitseinrichtung erstellt bereits so viele Exporte, wie sie gleichzeitig erstellen kann. Es ' +
    'ist nichts verloren. Der Vorgang wird automatisch wiederholt.',
  // The `423` family is FOUR sentences, not one. Until #776's follow-up every locked device produced the
  // „gesperrt" sentence below and the reason the record store had supplied was discarded — so a deleted
  // device, a device that never held anything and a device contradicting its own documentation were
  // indistinguishable on screen and in the log. These four mirror the server's split exactly; adding a code
  // there without a sentence here is what `tse-export.models.spec.ts` fails on.
  E_FISCAL_TSE_EXPORT_TSS_DELETED:
    'Diese Sicherheitseinrichtung wurde am Ablageort gelöscht. Ihre Daten für den gewählten Zeitraum lassen ' +
    'sich nicht mehr exportieren. Eine gelöschte Sicherheitseinrichtung kann nicht wiederhergestellt werden.',
  E_FISCAL_TSE_EXPORT_TSS_EVICTED:
    'Diese Sicherheitseinrichtung wurde zurückgezogen, bevor sie in Betrieb genommen wurde. Sie hat deshalb ' +
    'nichts aufgezeichnet, und es gibt aus ihr nichts zu exportieren. Es ist nichts verloren.',
  E_FISCAL_TSE_EXPORT_TSS_DEFECTIVE:
    'Diese Sicherheitseinrichtung meldet eine Störung und hat den Export abgelehnt. Das widerspricht der ' +
    'Beschreibung des Ablageorts, nach der auch eine gestörte Sicherheitseinrichtung exportiert werden kann. ' +
    'Bitte melden Sie diesen Fall der Administration.',
  E_FISCAL_TSE_EXPORT_TSS_LOCKED:
    'Diese Sicherheitseinrichtung ist gesperrt und hat keinen Export erzeugt. Der angegebene Grund ist hier ' +
    'nicht bekannt. Bitte wenden Sie sich an die Administration.',
  E_FISCAL_TSE_EXPORT_TSS_NOT_READY:
    'Diese Sicherheitseinrichtung kann im aktuellen Zustand keinen Export erzeugen. Bitte wenden Sie sich an ' +
    'die Administration.',
  E_FISCAL_TSE_EXPORT_TSS_NOT_FOUND:
    'Diese Sicherheitseinrichtung ist am Ablageort nicht mehr bekannt. Ihre Daten lassen sich nicht mehr ' +
    'exportieren.',

  // ----- the download -----
  E_FISCAL_TSE_EXPORT_NOT_AN_EXPORT:
    'Dieser Vorgang ist kein Export der Sicherheitseinrichtung.',
  E_FISCAL_TSE_EXPORT_NOT_READY:
    'Dieser Export ist noch nicht fertig. Sobald er bereitsteht, können Sie ihn herunterladen.',
  E_FISCAL_TSE_EXPORT_DOWNLOAD_UNAVAILABLE:
    'Der Export konnte gerade nicht abgerufen werden. Er ist nicht verloren. Bitte versuchen Sie es in Kürze ' +
    'erneut.',
  // „Weg" und „gerade nicht erreichbar" sind gegensätzliche Anweisungen. Hier tritt der erste Fall häufiger
  // auf als beim DSFinV-K-Export: Das erzeugte Archiv läuft am Ablageort nach einiger Zeit von selbst ab.
  E_FISCAL_TSE_EXPORT_DOWNLOAD_GONE:
    'Dieser Export ist am Ablageort nicht mehr vorhanden und kann nicht erneut heruntergeladen werden. Bitte ' +
    'erstellen Sie ihn für denselben Zeitraum neu.',
  E_FISCAL_TSE_EXPORT_DOWNLOAD_REFUSED:
    'Der Export konnte nicht abgerufen werden, weil der Zugriff auf den Datenspeicher abgelehnt wurde. Der ' +
    'Export ist unverändert vorhanden. Bitte wenden Sie sich an die Administration.',
  E_FISCAL_TSE_EXPORT_ARCHIVE_UNRECOGNISED:
    'Die abgerufene Datei ist kein lesbares Archiv und wurde deshalb nicht übergeben. Der Export selbst ist ' +
    'unverändert vorhanden. Bitte versuchen Sie es in Kürze erneut; hält es an, erstellen Sie den Export neu.',

  // ----- the engine -----
  E_FISCAL_JOB_TENANT_REQUIRED: 'Bitte wählen Sie zuerst einen Standort aus.',
  E_SESSION_SHARD_REQUIRED: 'Bitte wählen Sie zuerst einen Standort aus.',
  E_FISCAL_JOB_NO_ITEMS: 'Es wurde nichts gefunden, das exportiert werden könnte.',
  E_FISCAL_JOB_TOO_MANY_ITEMS:
    'Es wurden mehr Einträge gefunden, als ein Lauf umfassen darf. Bitte grenzen Sie den Zeitraum ein.',
  E_FISCAL_JOB_NOT_FOUND: 'Der Export wurde nicht gefunden. Bitte laden Sie die Seite neu.',
  E_FISCAL_JOB_ITEM_NOT_FOUND: 'Der Eintrag wurde nicht gefunden. Bitte laden Sie die Seite neu.',
  E_FISCAL_JOB_ITEM_NOT_RETRYABLE:
    'Dieser Eintrag wird gerade noch verarbeitet. Bitte warten Sie, bis er abgeschlossen ist.',
  E_FISCAL_JOB_ITEM_ALREADY_COMPLETED:
    'Dieser Eintrag ist bereits fertig. Die erstellte Datei steht weiterhin zur Verfügung; ein erneuter Lauf ' +
    'würde sie ersetzen.',
  E_FISCAL_JOB_NO_HANDLER:
    'Diese Art von Export steht auf dieser Installation nicht zur Verfügung. Bitte wenden Sie sich an die ' +
    'Administration.',
  E_FISCAL_JOB_OPERATION_FAILED:
    'Dieser Eintrag konnte nicht erstellt werden. Sie können ihn einzeln erneut versuchen.',
  E_FISCAL_JOB_OPERATION_CANCELLED: 'Die Erstellung dieses Eintrags wurde vorzeitig beendet.',
  E_FISCAL_JOB_OPERATION_EXPIRED:
    'Dieser Eintrag wurde nicht rechtzeitig fertig und muss neu angefordert werden.',
  E_FISCAL_JOB_OPERATION_WITHDRAWN:
    'Das Ergebnis dieses Eintrags ist nicht mehr verfügbar und muss neu erstellt werden.',
  E_FISCAL_JOB_POLL_BUDGET_EXHAUSTED:
    'Dieser Eintrag hat länger gedauert als erwartet und wird nicht mehr beobachtet. Sie können ihn einzeln ' +
    'erneut versuchen.',
  E_FISCAL_JOB_START_INTERRUPTED:
    'Der Start dieses Eintrags wurde unterbrochen. Bitte prüfen Sie ihn, bevor Sie ihn erneut versuchen.',
  E_FISCAL_JOB_INFRASTRUCTURE_UNAVAILABLE:
    'Ein benötigtes System war nicht erreichbar. Sie können diesen Eintrag erneut versuchen.'
};

function isIsoDay(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}
